/**
 * Admin LiveKit Monitor — fleet rooms, participants, host CPU/RAM/load.
 * Route: /admin/livekit-monitor (AD only via admin sidebar + backend verify_admin_access).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import {
  fetchLiveKitMonitor,
  fetchLiveKitRoomParticipants,
  type HostMetrics,
  type LiveKitMonitorSnapshot,
  type MonitorRoomRow,
  type RoomParticipant,
  type StaleLmsSession,
} from "./livekitMonitorApi";

const REFRESH_MS = 10000;

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  borderRight: "1px solid #e0e0e0",
  fontWeight: 600,
  fontSize: 12,
  padding: "8px 12px",
  color: "#666",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const bodyCellStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRight: "1px solid #e0e0e0",
  fontSize: 13,
  whiteSpace: "nowrap",
};

function formatUptime(seconds?: number): string {
  if (!seconds || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatLoad(load?: number[]): string {
  if (!load || !load.length) return "—";
  return load.map((n) => Number(n).toFixed(2)).join(" / ");
}

function statusColor(
  ok: boolean | undefined,
): "success" | "error" | "warning" | "default" {
  if (ok === true) return "success";
  if (ok === false) return "error";
  return "default";
}

function SummaryCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "ok" | "warn" | "bad" | "neutral";
}) {
  const border =
    tone === "ok"
      ? "#81c784"
      : tone === "warn"
        ? "#ffb74d"
        : tone === "bad"
          ? "#e57373"
          : "#e0e0e0";
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: `1px solid ${border}`,
        borderRadius: 1,
        minWidth: 140,
        flex: "1 1 140px",
        background: "#fff",
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#777", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography sx={{ fontSize: 11, color: "#999", mt: 0.5 }}>{hint}</Typography>
      ) : null}
    </Paper>
  );
}

function HostPanel({ host }: { host: HostMetrics }) {
  const cpu = host.cpu_percent ?? 0;
  const mem = host.memory?.percent ?? 0;
  const load = host.load_avg || [];
  const cpuTone = cpu >= 85 ? "bad" : cpu >= 60 ? "warn" : "ok";
  const memTone = mem >= 90 ? "bad" : mem >= 75 ? "warn" : "ok";
  const load1 = load[0] ?? 0;
  const cores = host.cpu_count || 1;
  const loadTone = load1 >= cores ? "bad" : load1 >= cores * 0.7 ? "warn" : "ok";

  return (
    <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          Server resources
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={host.ok ? "Metrics OK" : "Metrics error"}
            color={statusColor(host.ok)}
          />
          <Chip size="small" variant="outlined" label={host.source || "local"} />
          {host.hostname ? (
            <Chip size="small" variant="outlined" label={host.hostname} />
          ) : null}
        </Box>
      </Box>

      {!host.ok ? (
        <Typography color="error" sx={{ fontSize: 13 }}>
          {host.error || "Could not read host metrics"}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
            <SummaryCard
              label="CPU"
              value={`${cpu.toFixed(1)}%`}
              hint={`${host.cpu_count || "?"} cores`}
              tone={cpuTone}
            />
            <SummaryCard
              label="Load avg (1/5/15)"
              value={formatLoad(load)}
              hint={`vs ${cores} vCPU`}
              tone={loadTone}
            />
            <SummaryCard
              label="RAM"
              value={`${mem.toFixed(1)}%`}
              hint={
                host.memory
                  ? `${host.memory.used_mb.toFixed(0)} / ${host.memory.total_mb.toFixed(0)} MB`
                  : undefined
              }
              tone={memTone}
            />
            <SummaryCard
              label="Uptime"
              value={formatUptime(host.uptime_seconds)}
              tone="neutral"
            />
          </Box>

          {host.cpu_per_core && host.cpu_per_core.length > 0 ? (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
              {host.cpu_per_core.map((pct, idx) => (
                <Box
                  key={idx}
                  title={`Core ${idx}: ${pct.toFixed(1)}%`}
                  sx={{
                    width: 28,
                    height: 36,
                    borderRadius: 0.5,
                    border: "1px solid #ddd",
                    background: `linear-gradient(to top, ${
                      pct >= 85 ? "#e57373" : pct >= 60 ? "#ffb74d" : "#81c784"
                    } ${Math.min(100, pct)}%, #f5f5f5 ${Math.min(100, pct)}%)`,
                    fontSize: 9,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    pb: 0.25,
                    color: "#333",
                  }}
                >
                  {Math.round(pct)}
                </Box>
              ))}
            </Box>
          ) : null}

          {host.processes && host.processes.length > 0 ? (
            <TableContainer sx={{ maxHeight: 180, border: "1px solid #eee" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={headerCellStyle}>Process</TableCell>
                    <TableCell style={headerCellStyle}>PID</TableCell>
                    <TableCell style={headerCellStyle}>CPU %</TableCell>
                    <TableCell style={headerCellStyle}>RSS MB</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {host.processes.map((p) => (
                    <TableRow key={`${p.pid}-${p.name}`}>
                      <TableCell style={bodyCellStyle}>{p.name}</TableCell>
                      <TableCell style={bodyCellStyle}>{p.pid}</TableCell>
                      <TableCell style={bodyCellStyle}>
                        {(p.cpu_percent ?? 0).toFixed(1)}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {(p.rss_mb ?? 0).toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ fontSize: 12, color: "#888" }}>
              No LiveKit-related processes visible from this API host (common when the
              API runs in Docker without host PID namespace). CPU/RAM/load above still
              apply to this host.
            </Typography>
          )}

          {host.note ? (
            <Typography sx={{ fontSize: 11, color: "#999", mt: 1 }}>{host.note}</Typography>
          ) : null}
        </>
      )}
    </Paper>
  );
}

const LiveKitMonitor: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<LiveKitMonitorSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [search, setSearch] = useState("");
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);
  const [participantsByRoom, setParticipantsByRoom] = useState<
    Record<string, { loading: boolean; error?: string; rows: RoomParticipant[] }>
  >({});

  useEffect(() => {
    if (!sessionStorage.getItem("Admin")) {
      navigate("/");
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const snap = await fetchLiveKitMonitor();
      setData(snap);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          ? String(
              (err as { response?: { data?: { detail?: string } } }).response?.data
                ?.detail,
            )
          : "Failed to load LiveKit monitor";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      void load();
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, load]);

  const loadParticipants = useCallback(async (roomName: string) => {
    setParticipantsByRoom((prev) => ({
      ...prev,
      [roomName]: { loading: true, rows: prev[roomName]?.rows || [] },
    }));
    try {
      const res = await fetchLiveKitRoomParticipants(roomName);
      setParticipantsByRoom((prev) => ({
        ...prev,
        [roomName]: {
          loading: false,
          rows: res.participants || [],
          error: res.ok ? undefined : res.error || "Failed",
        },
      }));
    } catch {
      setParticipantsByRoom((prev) => ({
        ...prev,
        [roomName]: {
          loading: false,
          rows: [],
          error: "Failed to load participants",
        },
      }));
    }
  }, []);

  const toggleRoom = (roomName: string) => {
    setExpandedRoom((cur) => {
      const next = cur === roomName ? null : roomName;
      if (next) {
        void loadParticipants(next);
      }
      return next;
    });
  };

  const filteredRooms = useMemo(() => {
    const rooms = data?.rooms || [];
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (mismatchOnly && !r.mismatch) return false;
      if (!q) return true;
      const blob = [
        r.room_name,
        r.lms_session_id,
        r.session_name,
        r.session_author,
        r.recording_status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [data?.rooms, search, mismatchOnly]);

  const summary = data?.summary;

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>LiveKit Monitor</Typography>
          <Typography sx={{ fontSize: 12, color: "#777" }}>
            Live SFU rooms, LMS sessions, participants, and server CPU / RAM / load
            {data?.collected_at ? ` · Updated ${new Date(data.collected_at).toLocaleString()}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(_, v) => setAutoRefresh(v)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
          <Button variant="outlined" size="small" onClick={() => void load()}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <Spinner animation="border" />
        </Box>
      ) : null}

      {error ? (
        <Paper sx={{ p: 2, mb: 2, border: "1px solid #e57373" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : null}

      {summary ? (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          <SummaryCard
            label="SFU"
            value={summary.sfu_reachable ? "Up" : "Down"}
            hint={summary.credential_source || undefined}
            tone={summary.sfu_reachable ? "ok" : "bad"}
          />
          <SummaryCard
            label="SFU rooms"
            value={summary.active_sfu_rooms}
            tone="neutral"
          />
          <SummaryCard
            label="LMS live (LiveKit)"
            value={summary.active_lms_livekit_sessions}
            tone="neutral"
          />
          <SummaryCard
            label="Live participants"
            value={summary.total_live_participants}
            tone="neutral"
          />
          <SummaryCard
            label="Recording"
            value={summary.recording_sessions}
            tone={summary.recording_sessions > 0 ? "warn" : "neutral"}
          />
          <SummaryCard
            label="Mismatches"
            value={summary.mismatch_rooms + summary.stale_lms_sessions}
            hint={`${summary.stale_lms_sessions} LMS-only`}
            tone={
              summary.mismatch_rooms + summary.stale_lms_sessions > 0 ? "warn" : "ok"
            }
          />
        </Box>
      ) : null}

      {data?.host ? <HostPanel host={data.host} /> : null}

      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, mr: 1 }}>
            Active rooms
          </Typography>
          <TextField
            size="small"
            placeholder="Search room / session / trainer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={mismatchOnly}
                onChange={(_, v) => setMismatchOnly(v)}
                size="small"
              />
            }
            label="Mismatch only"
          />
        </Box>

        <TableContainer sx={{ maxHeight: 480, border: "1px solid #eee" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={headerCellStyle} />
                <TableCell style={headerCellStyle}>Room</TableCell>
                <TableCell style={headerCellStyle}>Session</TableCell>
                <TableCell style={headerCellStyle}>Trainer</TableCell>
                <TableCell style={headerCellStyle}>Participants</TableCell>
                <TableCell style={headerCellStyle}>Publishers</TableCell>
                <TableCell style={headerCellStyle}>Recording</TableCell>
                <TableCell style={headerCellStyle}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} style={bodyCellStyle}>
                    No active SFU rooms
                  </TableCell>
                </TableRow>
              ) : (
                filteredRooms.map((room: MonitorRoomRow) => {
                  const open = expandedRoom === room.room_name;
                  const partState = participantsByRoom[room.room_name];
                  return (
                    <React.Fragment key={room.room_name}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer", background: room.mismatch ? "#fff8e1" : undefined }}
                        onClick={() => toggleRoom(room.room_name)}
                      >
                        <TableCell style={bodyCellStyle}>{open ? "▾" : "▸"}</TableCell>
                        <TableCell style={bodyCellStyle}>{room.room_name}</TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.session_name || room.lms_session_id || "—"}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.session_author || "—"}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.num_participants}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.num_publishers}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.recording_status || "idle"}
                        </TableCell>
                        <TableCell style={bodyCellStyle}>
                          {room.mismatch ? (
                            <Chip size="small" color="warning" label="Mismatch" />
                          ) : (
                            <Chip size="small" color="success" label="OK" />
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 1.5, background: "#fafafa" }}>
                              {partState?.loading ? (
                                <Spinner animation="border" size="sm" />
                              ) : null}
                              {partState?.error ? (
                                <Typography color="error" sx={{ fontSize: 13 }}>
                                  {partState.error}
                                </Typography>
                              ) : null}
                              {!partState?.loading && !partState?.error ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell style={headerCellStyle}>Identity</TableCell>
                                      <TableCell style={headerCellStyle}>Name</TableCell>
                                      <TableCell style={headerCellStyle}>Role</TableCell>
                                      <TableCell style={headerCellStyle}>Mic</TableCell>
                                      <TableCell style={headerCellStyle}>Cam</TableCell>
                                      <TableCell style={headerCellStyle}>Waiting</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(partState?.rows || []).length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={6} style={bodyCellStyle}>
                                          No participants
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      (partState?.rows || []).map((p) => {
                                        const meta = (p.metadata || {}) as Record<
                                          string,
                                          unknown
                                        >;
                                        return (
                                          <TableRow key={p.identity}>
                                            <TableCell style={bodyCellStyle}>
                                              {p.identity}
                                            </TableCell>
                                            <TableCell style={bodyCellStyle}>
                                              {p.name || "—"}
                                            </TableCell>
                                            <TableCell style={bodyCellStyle}>
                                              {p.role || "—"}
                                            </TableCell>
                                            <TableCell style={bodyCellStyle}>
                                              {meta.microphone_muted === true
                                                ? "muted"
                                                : meta.microphone_muted === false
                                                  ? "on"
                                                  : "—"}
                                            </TableCell>
                                            <TableCell style={bodyCellStyle}>
                                              {meta.camera_muted === true
                                                ? "muted"
                                                : meta.camera_muted === false
                                                  ? "on"
                                                  : "—"}
                                            </TableCell>
                                            <TableCell style={bodyCellStyle}>
                                              {meta.waiting ? "yes" : "no"}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })
                                    )}
                                  </TableBody>
                                </Table>
                              ) : null}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {(data?.stale_lms_sessions || []).length > 0 ? (
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #ffb74d" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>
            LMS live without SFU room
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle}>Session ID</TableCell>
                  <TableCell style={headerCellStyle}>Name</TableCell>
                  <TableCell style={headerCellStyle}>Trainer</TableCell>
                  <TableCell style={headerCellStyle}>Expected room</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.stale_lms_sessions || []).map((row: StaleLmsSession) => (
                  <TableRow key={row.lms_session_id}>
                    <TableCell style={bodyCellStyle}>{row.lms_session_id}</TableCell>
                    <TableCell style={bodyCellStyle}>
                      {row.session_name || "—"}
                    </TableCell>
                    <TableCell style={bodyCellStyle}>
                      {row.session_author || "—"}
                    </TableCell>
                    <TableCell style={bodyCellStyle}>
                      {row.expected_room_name || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}
    </Box>
  );
};

export default LiveKitMonitor;
